import type { Sweep } from "@/types"
import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

/**
 * Merges Tailwind class names, resolving any conflicts.
 *
 * @param inputs - An array of class names to merge.
 * @returns A string of merged and optimized class names.
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs))
}

export const loadStoredFilters = (
  key: string,
  allowedValues: string[],
): string[] => {
  if (typeof window === "undefined") return []

  try {
    const stored = window.localStorage.getItem(key)
    if (!stored) return []

    const parsed = JSON.parse(stored)
    if (!Array.isArray(parsed)) return []

    const allowedSet = new Set(allowedValues)
    return Array.from(new Set(parsed.filter((value) => allowedSet.has(value))))
  } catch (error) {
    console.error("Failed to load filters from storage", error)
    return []
  }
}

export const persistFilters = (
  key: string,
  values: string[],
  allowedValues: string[],
): void => {
  if (typeof window === "undefined") return

  try {
    const allowedSet = new Set(allowedValues)
    const filtered = values.filter((value) => allowedSet.has(value))
    window.localStorage.setItem(key, JSON.stringify(filtered))
  } catch (error) {
    console.error("Failed to save filters to storage", error)
  }
}

export const getShowAlerts = (): boolean => {
  const show_alerts = JSON.parse(
    localStorage.getItem("auth_user") || "{}",
  ).show_alerts
  return show_alerts
}

export const formatDuration = (ms: number): string => {
  if (ms < 0) return "00:00:00"
  const totalSeconds = Math.floor(ms / 1000)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  return [hours, minutes, seconds]
    .map((unit) => String(unit).padStart(2, "0"))
    .join(":")
}

export const isStaffByRole = (obj: { role: number }): boolean => {
  if (obj.role > 2) return true
  return false
}

export const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleString()
}

export const formatDateOnly = (dateString: string) => {
  return new Date(dateString).toLocaleString().split(",")[0]
}

export function getOptimalBandwidth(
  startFreq: number,
  endFreq: number,
  maxBandwidth: number = 100,
): number {
  const width = endFreq - startFreq
  if (width <= 0) return maxBandwidth // or throw / return 1 / whatever makes sense

  const sweepCount = Math.ceil(width / maxBandwidth)
  const optimal = Math.ceil(width / sweepCount)

  // Optional: clamp to reasonable range (depends on your hardware rules)
  return Math.max(1, Math.min(maxBandwidth, optimal))
}

export function calculateSweeps(
  start_frequency: number,
  end_frequency: number,
  bandwidth: number,
): { sweeps: Sweep[]; bandwidth: number } {
  if (bandwidth <= 0) {
    throw new Error("Bandwidth must be > 0")
  }

  if (end_frequency <= start_frequency) {
    throw new Error("end_frequency must be > start_frequency")
  }

  const optimalBandwidth = getOptimalBandwidth(
    start_frequency,
    end_frequency,
    bandwidth,
  )

  const sweeps: Sweep[] = []

  let current = start_frequency

  while (current < end_frequency) {
    const next = Math.min(current + optimalBandwidth, end_frequency)
    sweeps.push([current, next])
    current = next
  }

  return {
    sweeps,
    bandwidth: optimalBandwidth,
  }
}
